/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

// consts and types
import {
  EMAIL_SIGNATURE_WIDTH,
  EMAIL_SIGNATURE_WIDTH_WITHOUT_IMAGE,
  EMAIL_SIGNATURE_WIDTH_WITH_MEDIUM_IMAGE,
  EMAIL_SIGNATURE_WIDTH_WITH_SMALL_IMAGE,
  EM_SIG_IMAGES_SIZES_DICT,
} from '@components/EmailSignaturev2/consts';
import type { EmailSignatureV2 } from '@components/EmailSignaturev2/types';
// hooks
import useNode from '@hooks/use-node';
import { getImageProxyLink } from '@modules/email-signatures-helpers';
// utils
import Emitter from '@modules/event-emitter';
import { sortBy } from 'es-toolkit';
// components
import { AnimatePresence, motion } from 'motion/react';
import { type FC, useEffect, useMemo } from 'react';

import styles from './EmailSignatureBanner.module.scss';

interface EmailSignatureBannerProps {
  emailSignature: EmailSignatureV2;
  disableAllSpecialEffects: boolean;
  isInEditingMode?: boolean;
}

const EmailSignatureBanner: FC<React.PropsWithChildren<EmailSignatureBannerProps>> = ({
  emailSignature,
  disableAllSpecialEffects,
  isInEditingMode,
}) => {
  const [bannerNode, bannerNodeRef] = useNode<HTMLTableElement>();

  const bannerWidth = useMemo(() => {
    const images = emailSignature.images.filter((image) => image.imageUrl);
    if (images.length === 0) {
      return EMAIL_SIGNATURE_WIDTH_WITHOUT_IMAGE;
    } else {
      const biggestImage = sortBy(images, [
        (image) => EM_SIG_IMAGES_SIZES_DICT[image.size] * -1,
      ])[0];
      if (biggestImage.size === 'small') {
        return EMAIL_SIGNATURE_WIDTH_WITH_SMALL_IMAGE;
      } else if (biggestImage.size === 'medium') {
        return EMAIL_SIGNATURE_WIDTH_WITH_MEDIUM_IMAGE;
      } else {
        return EMAIL_SIGNATURE_WIDTH;
      }
    }
  }, [emailSignature.images]);

  useEffect(() => {
    if (!bannerNode) return;
    // scroll into view when the banner is first created
    bannerNode.scrollIntoView();
  }, [bannerNode]);

  const imageLink = useMemo(() => {
    if (isInEditingMode) {
      return emailSignature.banner?.imageUrl;
    } else {
      // if it is for testing, use firebase url since the admin may have not saved the signature and the proxy links have not been created yet
      return getImageProxyLink(emailSignature.banner?.id || '');
    }
  }, [emailSignature.banner?.id, emailSignature.banner?.imageUrl, isInEditingMode]);

  return (
    <tr>
      <td>
        <table
          ref={bannerNodeRef}
          cellPadding={0}
          cellSpacing={0}
          style={{
            width: `${bannerWidth}px`,
            background: 'transparent',
          }}
        >
          <tbody>
            <tr>
              <td
                style={{
                  height: '28px',
                  width: `${bannerWidth}px`,
                }}
              ></td>
            </tr>
            <tr>
              <td
                style={{
                  textAlign: 'left',
                  paddingBottom: '-28px',
                  background: 'transparent',
                }}
                role={disableAllSpecialEffects ? undefined : 'gridcell'}
                className={
                  disableAllSpecialEffects ? undefined : styles['interactive-preview-image']
                }
                onClick={(event) => {
                  event.preventDefault();
                  Emitter.emit('SCROLL_TO_BANNER');
                }}
                onKeyDown={(event: React.KeyboardEvent) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    Emitter.emit('SCROLL_TO_BANNER');
                  } else if (event.key === ' ') {
                    event.preventDefault();
                  }
                }}
              >
                {emailSignature.banner?.externalLink && (
                  <a
                    style={{ background: 'transparent' }}
                    href={emailSignature.banner?.externalLink}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(event) => {
                      event.preventDefault();
                      Emitter.emit('SCROLL_TO_BANNER');
                    }}
                    onKeyDown={(event: React.KeyboardEvent) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        Emitter.emit('SCROLL_TO_BANNER');
                      } else if (event.key === ' ') {
                        event.preventDefault();
                      }
                    }}
                  >
                    <img
                      width={`${bannerWidth}`}
                      style={{
                        width: `${bannerWidth}px`,
                        minHeight: '10px',
                      }}
                      src={imageLink}
                      alt="Email Signature Banner"
                    />
                  </a>
                )}
                {!emailSignature.banner?.externalLink && (
                  <img
                    width={`${bannerWidth}`}
                    style={{
                      width: `${bannerWidth}px`,
                      minHeight: '10px',
                    }}
                    src={imageLink}
                    alt="Email Signature Banner"
                  />
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </td>
    </tr>
  );
};

type EmailSignatureDummyBannerProps = {
  show: boolean;
  emailSignature: EmailSignatureV2;
};

const EmailSignatureDummyBanner: FC<React.PropsWithChildren<EmailSignatureDummyBannerProps>> = ({
  show,
  emailSignature,
}) => {
  const [bannerNode, bannerNodeRef] = useNode<HTMLTableElement>();

  const bannerWidth = useMemo(() => {
    const images = emailSignature.images.filter((image) => image.imageUrl);
    if (images.length === 0) {
      return EMAIL_SIGNATURE_WIDTH_WITHOUT_IMAGE;
    } else {
      const biggestImage = sortBy(images, [
        (image) => EM_SIG_IMAGES_SIZES_DICT[image.size] * -1,
      ])[0];
      if (biggestImage.size === 'small') {
        return EMAIL_SIGNATURE_WIDTH_WITH_SMALL_IMAGE;
      } else if (biggestImage.size === 'medium') {
        return EMAIL_SIGNATURE_WIDTH_WITH_MEDIUM_IMAGE;
      } else {
        return EMAIL_SIGNATURE_WIDTH;
      }
    }
  }, [emailSignature.images]);

  useEffect(() => {
    if (!bannerNode) return;
    bannerNode.scrollIntoView();
  }, [bannerNode]);

  return (
    <AnimatePresence>
      {show && (
        <tr>
          <td>
            <motion.table
              ref={bannerNodeRef}
              cellPadding={0}
              cellSpacing={0}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1 }}
            >
              <tbody>
                <tr>
                  <td style={{ height: '28px', width: `${bannerWidth}px` }}></td>
                </tr>
                <tr>
                  <td style={{ textAlign: 'left' }}>
                    <img
                      width={`${bannerWidth}px`}
                      src={'/img/email-signature-banner-example.png'}
                      alt="Email Signature Banner"
                    />
                  </td>
                </tr>
              </tbody>
            </motion.table>
          </td>
        </tr>
      )}
    </AnimatePresence>
  );
};

export default EmailSignatureBanner;

export { EmailSignatureDummyBanner };
